/**
 * Название бренда, собранное из сотен крошечных валютных обозначений (EUR, USD, ₿, ¥ …), —
 * подпись главного экрана в духе референса «созвездие частиц» (просьба заказчика 2026-09-17).
 * Частицы циклично разлетаются и снова собираются в надпись.
 *
 * Только canvas 2D и requestAnimationFrame; во вкладке не на виду и под prefers-reduced-motion
 * анимация стоит (надпись остаётся собранной). Имя бренда приходит из конфига — в коде его нет.
 */
import { useEffect, useRef } from 'react';

/** Что рисуем вместо точек: тикеры и знаки валют. */
const GLYPHS = ['EUR', 'USD', 'GBP', 'JPY', 'AUD', 'CHF', 'CAD', 'NZD', 'BTC', 'ETH', '$', '€', '£', '¥', '₿', '₣'];
/** Палитра референса: фиолетовый, янтарный, бирюзовый, маджента, голубой, белый. */
const COLORS = ['#ffffff', '#ffffff', '#ffffff', '#ffffff', '#ffffff', '#8a5cff', '#8a5cff', '#ffb829', '#45c8ff', '#ff5ce1', '#15846e'];

const HOLD_MS = 3_200;
const FLY_MS = 1_900;
const SCATTER_HOLD_MS = 1_400;
const CYCLE_MS = HOLD_MS + FLY_MS + SCATTER_HOLD_MS + FLY_MS;

interface Particle {
  hx: number;
  hy: number;
  sx: number;
  sy: number;
  glyph: string;
  color: string;
  size: number;
  /** Сдвиг фазы: разлетаются и собираются не строем, а волной. */
  delay: number;
  drift: number;
}

const easeInOut = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

/** Точки надписи: рисуем текст на скрытом холсте и берём заполненные пиксели по сетке. */
function sampleText(text: string, width: number, height: number, step: number): Array<[number, number]> {
  const off = document.createElement('canvas');
  off.width = width;
  off.height = height;
  const ctx = off.getContext('2d');
  if (!ctx) return [];
  let fontSize = Math.floor(height * 0.9);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  do {
    ctx.font = `800 ${fontSize}px "Inter Variable", Inter, system-ui, sans-serif`;
    fontSize -= 4;
  } while (ctx.measureText(text).width > width * 0.97 && fontSize > 12);
  ctx.fillStyle = '#fff';
  ctx.fillText(text, width / 2, height / 2);
  const data = ctx.getImageData(0, 0, width, height).data;
  const points: Array<[number, number]> = [];
  for (let y = 0; y < height; y += step) {
    for (let x = 0; x < width; x += step) {
      // сетка дробная — пиксель берём по целым координатам, иначе индекс мимо буфера
      const px = Math.round(x);
      const py = Math.round(y);
      if ((data[(py * width + px) * 4 + 3] ?? 0) > 128) points.push([x + (Math.random() - 0.5) * 1.5, y + (Math.random() - 0.5) * 1.5]);
    }
  }
  return points;
}

export function BrandConstellation({ text, height = 104 }: { text: string; height?: number }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !text) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const reduceMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    let particles: Particle[] = [];
    let width = 0;
    let frame = 0;
    let running = true;
    const started = performance.now();

    const build = (): void => {
      const dpr = Math.min(globalThis.devicePixelRatio || 1, 2);
      width = canvas.clientWidth;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // плотность частиц — под ширину экрана: ~700 на телефоне
      // сетка плотная: буквы на ширине телефона невысокие, и точки должны их прорисовать
      const step = width < 360 ? 3.2 : 2.9;
      particles = sampleText(text.toUpperCase(), width, height, step).map(([hx, hy]) => ({
        hx,
        hy,
        sx: Math.random() * width,
        sy: Math.random() * height,
        glyph: GLYPHS[Math.floor(Math.random() * GLYPHS.length)] ?? '$',
        color: COLORS[Math.floor(Math.random() * COLORS.length)] ?? '#8052ff',
        size: 1.9 + Math.random() * 0.7,
        delay: Math.random() * 0.35,
        drift: Math.random() * Math.PI * 2,
      }));
    };

    const draw = (now: number): void => {
      if (!running) return;
      const t = (now - started) % CYCLE_MS;
      ctx.clearRect(0, 0, width, height);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      for (const p of particles) {
        // фаза частицы: 0 — собрана, 1 — разлетелась
        let k = 0;
        if (!reduceMotion) {
          if (t < HOLD_MS) k = 0;
          else if (t < HOLD_MS + FLY_MS) k = easeInOut(Math.min(1, Math.max(0, (t - HOLD_MS) / FLY_MS - p.delay) / (1 - p.delay)));
          else if (t < HOLD_MS + FLY_MS + SCATTER_HOLD_MS) k = 1;
          else k = 1 - easeInOut(Math.min(1, Math.max(0, (t - HOLD_MS - FLY_MS - SCATTER_HOLD_MS) / FLY_MS - p.delay) / (1 - p.delay)));
        }
        // в разлёте частицы чуть дышат, чтобы облако жило
        const wobble = k * 6;
        const x = p.hx + (p.sx - p.hx) * k + Math.sin(now / 900 + p.drift) * wobble;
        const y = p.hy + (p.sy - p.hy) * k + Math.cos(now / 1100 + p.drift) * wobble;
        ctx.globalAlpha = 0.55 + 0.45 * (1 - k) * 0.8 + 0.1 * Math.sin(now / 500 + p.drift);
        ctx.fillStyle = p.color;
        // в собранном виде — точки, в разлёте — читаемые тикеры
        ctx.font = `600 ${p.size + k * 4.5}px "Inter Variable", Inter, system-ui, sans-serif`;
        ctx.fillText(p.glyph, x, y);
      }
      ctx.globalAlpha = 1;
      frame = requestAnimationFrame(draw);
    };

    build();
    // до загрузки шрифта надпись мерится запасным — пересобираем, когда Inter готов
    void document.fonts?.ready.then(() => build());
    frame = requestAnimationFrame(draw);

    const onVisibility = (): void => {
      running = document.visibilityState !== 'hidden';
      if (running) frame = requestAnimationFrame(draw);
      else cancelAnimationFrame(frame);
    };
    const observer = new ResizeObserver(() => build());
    observer.observe(canvas);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      running = false;
      cancelAnimationFrame(frame);
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [text, height]);

  return <canvas ref={canvasRef} className="brand-constellation" style={{ height }} aria-label={text} role="img" />;
}
